<template>
  <form
      class="form"
      enctype="multipart/form-data"
      novalidate
      @submit.prevent="handleSubmit"
  >
    <FormRow>
      <FormFiles
          :ref="addFormInputRef"
          v-model="files"
          :default-selected-files="document?.name != null ? [document.name] : []"
          :is-multiple="!isEditMode"
          :label="t('document.label')"
          :min="1"
          name="document"
          @change="onFileChange"
          @validated="handleValidation"
      />
    </FormRow>
    <FormRow>
      <FormSelect
          :ref="addFormInputRef"
          v-model="category"
          :label="t('document.category')"
          :options="documentCategories"
          name="category"
          @change="onCategoryChange"
          @validated="handleValidation"
      />
    </FormRow>
    <FormRow>
      <FormInput
          :ref="addFormInputRef"
          v-model="date"
          :label="t('document.date')"
          :rules="[]"
          name="date"
          type="date"
          @validated="handleValidation"
      />
    </FormRow>
    <div>
      <FormRadios
          :ref="addFormInputRef"
          v-model="policy"
          :options="documentPolicies"
          :title="t('document.policy')"
          name="radios"
          @change="onPolicyChange"
          @validated="handleValidation"
      />
    </div>
    <div class="form__submit">
      <button class="btn btn--fullscreen" type="submit">
        {{ t("global.save") }}
      </button>
      <button
          v-if="isEditMode"
          class="btn btn--red btn--fullscreen"
          type="button"
          @click="deleteDocument"
      >
        {{ t("global.delete") }}
      </button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import {useI18n} from "vue3-i18n";
import {computed, onMounted, ref} from "vue";
import FormRow from "@/components/forms/FormRow.vue";
import {Document, IAddDocumentsRequest} from "@/types/entities/document";
import {DocumentCategoryHelper, DocumentPolicy, Guid, Role} from "@/types";
import {DocumentPolicyHelper} from "@/types/documentPolicy";
import {useDocumentService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";
import {useRouter} from "vue-router";
import {Status} from "@/validation";
import FormInput from "@/components/forms/FormInput.vue";
import FormRadios from "@/components/forms/FormRadios.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import {SucceededOrNotResponse} from "@/types/responses";
import {useMemberStore} from "@/stores/memberStore";

// eslint-disable-next-line no-undef
const props = defineProps<{
  id: string
}>()

const {t} = useI18n();

const router = useRouter();
const documentService = useDocumentService();

const memberStore = useMemberStore();
const canSeeAllPolicies = memberStore.hasOneOfTheseRoles([Role.Admin, Role.CorporateAdmin, Role.RegularSyndicateAdmin, Role.RegularSyndicateManager])

const isEditMode = Guid.isValid(props.id);
const document = ref<Document>() //useful for the edit state
const files = ref<any>() //useful for the add state
const policy = ref<number | string>(0)
const category = ref<string>()
const date = ref<string>("")

onMounted(async () => {
  if (!isEditMode) return

  const doc = await documentService.getDocument(props.id);

  document.value = doc;
  files.value = doc.document;
  policy.value = doc.policy ?? DocumentPolicy.Private;
  category.value = doc.category;
  date.value = doc.date ?? "";
})

const documentPolicies = computed(() => {
  if (canSeeAllPolicies) return DocumentPolicyHelper.policiesValueAndNames()
  return [DocumentPolicyHelper.policiesValueAndNames()[0]]
});

function onPolicyChange(event: any) {
  if (isEditMode && document.value) document.value.policy = event.target.value;
}

const documentCategories = computed(() =>
    DocumentCategoryHelper.categoriesNameAndLabel().sort((a, b) => a.label > b.label ? 1 : -1)
);

function onCategoryChange(event: any) {
  if (isEditMode && document.value) document.value.category = event.target.value;
}

function onFileChange(event: any) {
  if (isEditMode && document.value) document.value.document = event.target.files[0];
}

//Validation -------
const formInputs = ref<(typeof FormInput)[]>([]);
const inputValidationStatuses: any = {};

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref)) formInputs.value.push(ref);
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid;
}

//Submit ------------
async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  } else {
    await save();
  }
}

async function save() {
  let result: any;

  if (isEditMode) {
    // an empty date must be omitted from the multipart payload, "" fails DateTime? binding
    if (document.value) document.value.date = date.value || undefined;
    result = await documentService.updateDocument(document.value as Document);
  } else {
    result = await documentService.addDocuments({
      policy: policy.value,
      category: category.value,
      date: date.value || undefined,
      // don't change Documents for documents, it makes model binding work in back-end
      Documents: files.value
    } as IAddDocumentsRequest);
  }

  if (result?.id || result[0]?.id) {
    notifySuccess(files.value?.length > 1
        ? t("document.saveMultipleSuccess").replace("{length}", files.value.length)
        : t("document.saveSuccess")
    );
    setTimeout(() => {
      router.back();
    }, 1500);
  } else {
    notifyError(files.value?.length > 1
        ? t("document.saveMultipleError")
        : t("document.saveError")
    );
  }
}

async function deleteDocument() {
  if (!Guid.isValid(props.id ?? "") || props.id == null) return;

  let confirmDelete = confirm(t("document.deleteConfirmation"));

  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await documentService.deleteDocument(props.id);
  if (result?.succeeded) {
    notifySuccess(t('document.deleteSuccess'))
    router.back();
  } else {
    notifyError(t('document.deleteError'))
  }
}
</script>